import { TaskStatus, normalizeTaskStatus, type Task } from "@/types";

/**
 * Where a task's "related record" link goes (#4243 follow-up).
 *
 * Most tasks open the record's show page. A deductible-review task (Feature
 * #3502) deep-links to the claimant's Deductibles tab for the review's policy
 * with the resolution dialog open, because that is the only place the work can
 * be done. The review id and policy come from the task metadata the backend
 * writes (`deductible_review_id`, `policy_id`). Once the task is closed the
 * review is too, so the link still lands on the Deductibles tab (where the
 * decision is listed) but no longer asks for the dialog.
 */
export const REVIEW_QUERY_PARAM = "review";
export const REVIEW_POLICY_QUERY_PARAM = "policy";

const metaString = (task: Pick<Task, "metadata">, key: string): string | undefined => {
  const v = task.metadata?.[key];
  return typeof v === "string" && v ? v : undefined;
};

export const isDeductibleReviewTask = (task: Pick<Task, "metadata" | "record">): boolean =>
  task.record?.object_type === "claimants" && !!metaString(task, "deductible_review_id");

type TaskLinkFields = Pick<Task, "metadata" | "record" | "policy_id_ref"> & Partial<Pick<Task, "status" | "completed">>;

/**
 * A deductible-review task still waiting on someone: open or in progress, not
 * completed (a cancelled task is not completed, but its review is not waiting).
 */
export const isOpenDeductibleReviewTask = (task?: TaskLinkFields): boolean => {
  if (!task || !isDeductibleReviewTask(task) || task.completed) return false;
  const status = normalizeTaskStatus(task.status) ?? TaskStatus.OPEN;
  return status === TaskStatus.OPEN || status === TaskStatus.IN_PROGRESS;
};

/** In-app path (no leading `#`). Undefined when the task has no record. */
export const taskRecordPath = (task: TaskLinkFields): string | undefined => {
  const r = task.record;
  if (!r?.object_type || !r?.object_id) return undefined;
  const base = `/${r.object_type.replace(/_/g, "-")}/${r.object_id}/show`;
  if (!isDeductibleReviewTask(task)) return base;
  // A closed review task: no `?review=open`, which would only toast "no open review".
  if (!isOpenDeductibleReviewTask(task)) return `${base}/deductibles`;
  const policyId = metaString(task, "policy_id") ?? task.policy_id_ref;
  const params = new URLSearchParams({ [REVIEW_QUERY_PARAM]: "open" });
  if (policyId) params.set(REVIEW_POLICY_QUERY_PARAM, policyId);
  return `${base}/deductibles?${params.toString()}`;
};
